/**
 * OneStamp web — query key factory.
 *
 * EVERY React Query call MUST source its key from this factory. Keys are
 * structured tuples so the global cache stays brand-isolated and a brand
 * switch (Super Admin) or logout cleanly invalidates everything.
 *
 * Forbidden:
 *   useQuery({ queryKey: ['dashboard'], ... })   // free-form strings
 *   useQuery({ queryKey: ['branches'], ... })    // missing brand_id
 *
 * Required:
 *   useQuery({ queryKey: qk.ownerBranches(brandId), ... })
 */
export const qk = {
  // ---- Brand owner — server derives brand_id from JWT, but the cache
  // key still carries it so concurrent brand contexts (impossible today
  // but cheap to make safe) never collide.
  ownerOverview: (brandId, range) => ['owner-overview', brandId, range],
  ownerRecentActivity: (brandId, limit) => ['owner-recent-activity', brandId, limit],
  ownerAlerts: (brandId) => ['owner-alerts', brandId],
  ownerBrand: (brandId) => ['owner-brand', brandId],
  ownerBranches: (brandId) => ['owner-branches', brandId],
  ownerBranchDetail: (brandId, branchId) => ['owner-branch-detail', brandId, branchId],
  ownerCashiers: (brandId) => ['owner-cashiers', brandId],
  ownerRewards: (brandId) => ['owner-rewards', brandId],
  ownerPromotions: (brandId) => ['owner-promotions', brandId],
  ownerReviewLinks: (brandId) => ['owner-review-links', brandId],
  ownerReviewAnalytics: (brandId, range) => ['owner-review-analytics', brandId, range],
  ownerBirthdayPromotions: (brandId) => ['owner-birthday-promotions', brandId],
  ownerGallery: (brandId) => ['owner-gallery', brandId],
  ownerAuditLogs: (brandId, filters) => ['owner-audit', brandId, filters],

  // ---- EX-C Step 5 / 6 — Inactive customer detection
  // Owner key: server scopes to JWT brand_id, but the cache key still
  // carries (days, limit, offset) so pagination and threshold changes
  // never collide. Admin key (Step 6) is parameterised on brand_id too.
  ownerInactive: (days, limit, offset) =>
    ['owner-inactive-customers', days, limit, offset],
  adminBrandInactive: (brandId, days, limit, offset) =>
    ['admin-brand-inactive-customers', brandId, days, limit, offset],

  // ---- Phase 5B.1 — Customer CRM (Owner)
  ownerCrmCustomers: (brandId, filters) => ['owner-crm-customers', brandId, filters],
  ownerCrmKpis: (brandId) => ['owner-crm-kpis', brandId],
  ownerCrmCustomer: (brandId, customerId) => ['owner-crm-customer', brandId, customerId],
  ownerCrmCustomerTransactions: (brandId, customerId, filters) =>
    ['owner-crm-customer-transactions', brandId, customerId, filters],
  ownerCrmCustomerRewards: (brandId, customerId, filters) =>
    ['owner-crm-customer-rewards', brandId, customerId, filters],
  ownerCrmSettings: (brandId) => ['owner-crm-settings', brandId],

  // ---- Super admin platform-wide
  adminOverview: () => ['admin-overview'],
  adminPlatformOverview: () => ['admin-platform-overview'], // legacy alias
  adminBrands: () => ['admin-brands'],
  adminBrand: (brandId) => ['admin-brand', brandId],
  adminBrandDetail: (brandId) => ['admin-brand-detail', brandId], // legacy alias
  adminBrandAudit: (brandId) => ['admin-brand-audit', brandId],
  // EX-B Step 6 — per-brand drilldown analytics key (range is part of the
  // tuple so concurrent windows never collide in cache).
  adminBrandOverview: (brandId, range) => ['admin-brand-overview', brandId, range],
  adminOwners: () => ['admin-owners'],
  adminCategories: (opts) => ['admin-categories', opts ?? {}],
  categories: () => ['categories'],
  adminAudit: (filters) => ['admin-audit', filters],
  adminAuditLogs: (filters) => ['admin-audit-logs', filters], // legacy alias
  adminEventFailures: (filters) => ['admin-event-failures', filters],

  // ---- Phase 5B.1 — Customer CRM (Super Admin)
  adminCrmCustomers: (brandId, filters) => ['admin-crm-customers', brandId, filters],
  adminCrmKpis: (brandId) => ['admin-crm-kpis', brandId],
  adminCrmCustomer: (brandId, customerId) => ['admin-crm-customer', brandId, customerId],
  adminCrmCustomerTransactions: (brandId, customerId, filters) =>
    ['admin-crm-customer-transactions', brandId, customerId, filters],
  adminCrmCustomerRewards: (brandId, customerId, filters) =>
    ['admin-crm-customer-rewards', brandId, customerId, filters],
  adminCrmSettings: (brandId) => ['admin-crm-settings', brandId],

  // ---- Auth
  authMe: () => ['auth-me'],

  // ---- PHASE W-1 — Customer Web Shell
  customerBrands: (opts) => ['customer-brands', opts ?? {}],
  customerBrandsFeatured: () => ['customer-brands-featured'],
  customerBrand: (brandId) => ['customer-brand', brandId],
  customerBrandBranches: (brandId) => ['customer-brand', brandId, 'branches'],
  customerBrandRewards: (brandId) => ['customer-brand', brandId, 'rewards'],
  // Phase 2c — rewards catalog + redemption. `opts` carries any
  // server-side filters (e.g. `{ brand_id }`); calling with no opts
  // yields the cross-brand catalog tuple.
  customerMyRewards: (opts) => ['customer-my-rewards', opts ?? {}],
  // Phase 2d — read-only customer surfaces.
  customerRedemptions: () => ['customer-redemptions'],
  customerNotifications: () => ['customer-notifications'],
  customerNotificationsUnread: () => ['customer-notifications-unread'],
  customerCards: () => ['customer-cards'],
  // Phase 2b — recent-activity key now opts-aware so the Card Detail
  // brand-scoped feed and the Home unfiltered feed don't collide. The
  // legacy HomePage call `qk.customerRecentActivity(10)` still works
  // and yields a stable, distinct tuple from any opts-object form.
  customerRecentActivity: (optsOrLimit) => {
    const opts =
      typeof optsOrLimit === 'number'
        ? { limit: optsOrLimit }
        : (optsOrLimit ?? {});
    return ['customer-recent-activity', opts];
  },
  // Phase 2b — paginated transaction history (filters as opts).
  customerTransactionsHistory: (opts) => [
    'customer-transactions-history',
    opts ?? {},
  ],
  customerQrSession: (cardId) => ['customer-qr-session', cardId],
  customerFavorites: () => ['customer-favorites'],

  // Phase W-2 — promotions
  customerPromotions: (opts) => ['customer-promotions', opts ?? {}],
  customerPromotionDetail: (id) => ['customer-promotion-detail', id],

  // ---- Phase R3 / R4 — Receipt OCR Claim --------------------------------
  // Customer surface
  customerReceiptClaims: (opts) => ['customer-receipt-claims', opts ?? {}],
  customerReceiptClaim: (claimId) => ['customer-receipt-claim', claimId],
  // Owner surface — brand_id derived server-side from JWT; cache key
  // still carries filter opts so Pending/Approved/Rejected tabs don't
  // collide.
  ownerReceiptClaims: (opts) => ['owner-receipt-claims', opts ?? {}],
  ownerReceiptClaim: (claimId) => ['owner-receipt-claim', claimId],
  ownerReceiptClaimsPendingCount: () => ['owner-receipt-claims-pending-count'],
  // Phase R10.3 — dashboard aggregates
  ownerReceiptClaimsCounts: (opts) => ['owner-receipt-claims-counts', opts ?? {}],
  ownerReceiptClaimsBranchPerf: (opts) => ['owner-receipt-claims-branch-perf', opts ?? {}],
  ownerBranchesForFilter: (brandId) => ['owner-branches-for-filter', brandId],
  ownerBrandSettings: (brandId) => ['owner-brand-settings', brandId],

  // ---- OneStamp Pro Phase 1 — Memberships ----
  ownerMembershipDashboard: (brandId) => ['owner-membership-dashboard', brandId],
  ownerMembershipTiers: (brandId) => ['owner-membership-tiers', brandId],
  ownerMembershipPlans: (brandId) => ['owner-membership-plans', brandId],
  ownerMembershipBenefits: (planId) => ['owner-membership-benefits', planId],
  ownerMembershipMembers: (brandId, filters) =>
    ['owner-membership-members', brandId, filters ?? {}],
  ownerMembershipMember: (membershipId) => ['owner-membership-member', membershipId],
  ownerCustomerMemberships: (customerId) => ['owner-customer-memberships', customerId],
  customerMyMemberships: () => ['customer-my-memberships'],

  // Merchant Pro V1 PR-2 — Dashboard aggregator + Reservations.
  // One key per surface; brand_id derived server-side from JWT so
  // the queryKey does not embed it (matches existing convention).
  ownerDashboardToday: () => ['owner', 'dashboard', 'today'],
  ownerReservations: (params = {}) => ['owner', 'reservations', 'list', params],
  ownerReservation: (reservationId) => ['owner', 'reservations', 'detail', reservationId],

  // ---- Merchant Pro V1 PR-3 — Walk-in / Waitlist + Manual Booking
  ownerWaitlistBuckets: (params = {}) => ['owner', 'waitlists', 'buckets', params],
  ownerCrmSearch: (search) => ['owner', 'crm', 'search', search || ''],
  brandBranches: (brandId) => ['brand-branches', brandId],
  branchRooms: (branchId) => ['branch-rooms', branchId],
  roomTables: (roomId) => ['room-tables', roomId],

  // ---- Merchant Pro V1 PR-4a — Live Table State
  ownerFloorPlanState: (branchId) => ['owner', 'floor-plan', 'state', branchId],

  // ---- Merchant Pro V1 PR-4b — Owner Notification Center
  ownerNotifications: (params = {}) => ['owner', 'notifications', 'list', params],
  ownerNotificationsUnreadCount: () => ['owner', 'notifications', 'unread-count'],
};

/**
 * Predicate factory for invalidating every cached entry whose key
 * includes the given brand_id. Used when:
 *   - Super Admin switches the drilled brand
 *   - Brand activates / deactivates (invalidate platform overview too)
 */
export const matchByBrand = (brandId) => (query) =>
  Array.isArray(query.queryKey) && query.queryKey.includes(brandId);
